import React from 'react'
import './VarianceGauge.scss'

interface VarianceGaugeProps {
  variance: number | null
  pumpSales?: number
  tankMovement?: number
  tolerance?: number
}

const formatLitres = (val: number): string => {
  const abs = Math.abs(val)
  return abs.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

const VarianceGauge: React.FC<VarianceGaugeProps> = ({
  variance,
  pumpSales,
  tankMovement,
  tolerance = 1.0,
}) => {
  const hasVariance = variance !== null && Number.isFinite(variance)
  const value = variance ?? 0
  const isDeficit = hasVariance && value < -tolerance
  const isExcess = hasVariance && value > tolerance
  const isBalanced = hasVariance && !isDeficit && !isExcess

  // 0L points straight UP at apex (0 deg). Negative tilts left (red deficit), positive tilts right (green excess).
  const rawAngle = (value / 50) * 60
  const clampedAngle = Math.max(-75, Math.min(75, rawAngle))

  let tone: 'danger' | 'success' | 'amber' = 'amber'
  let displayValue = `${variance} L`

  if (isDeficit) {
    tone = 'danger'
    displayValue = `-${formatLitres(value)} L`
  } else if (isExcess) {
    tone = 'success'
    displayValue = `+${formatLitres(value)} L`
  } else {
    tone = 'amber'
    displayValue = value === 0 ? '0.0 L' : `${value > 0 ? '+' : '-'}${formatLitres(value)} L`
  }

  if (!hasVariance) displayValue = 'Not available'

  return (
    <div className='bx-variance-gauge'>
      <div className='bx-gauge-svg-wrap'>
        <svg viewBox='0 0 219 94' fill='none' aria-hidden='true'>
          <defs>
            <mask
              id='bx-gauge-mask'
              maskUnits='userSpaceOnUse'
              x='24'
              y='0'
              width='170'
              height='85'
            >
              <path
                d='M187.603 84.8477C191.244 84.8477 194.222 81.8913 193.939 78.2613C192.376 58.1682 183.696 39.2036 169.344 24.8513C153.432 8.93928 131.851 1.69893e-06 109.348 0C86.8447 -1.69893e-06 65.2633 8.93928 49.3513 24.8513C34.999 39.2036 26.3195 58.1682 24.7558 78.2613C24.4733 81.8913 27.4516 84.8476 31.0925 84.8476V84.8476C34.7335 84.8476 37.6536 81.8895 37.9879 78.2639C39.5181 61.6726 46.7959 46.0532 58.6746 34.1746C72.1139 20.7352 90.3416 13.1851 109.348 13.1851C128.354 13.1851 146.581 20.7352 160.021 34.1746C171.899 46.0532 179.177 61.6726 180.707 78.2639C181.042 81.8895 183.962 84.8477 187.603 84.8477V84.8477Z'
                fill='#ffffff'
              />
            </mask>
          </defs>

          {/* Three segmented color zones */}
          <g mask='url(#bx-gauge-mask)'>
            {/* Left Deficit (Red) */}
            <path
              d='M106.02 -186.321L107.587 111H-20.5L106.02 -186.321Z'
              fill='var(--mgr-danger)'
            />
            {/* Center Amber vertical bar */}
            <path d='M109 108.321L112.5 -189H107L109 108.321Z' fill='var(--mgr-amber)' />
            {/* Right Excess (Green) */}
            <path d='M113.48 -186.321L238.5 111H110.413L113.48 -186.321Z' fill='var(--mgr-green)' />
          </g>

          {/* Left Minus glyph */}
          <path d='M5.484 79.14V80.064H0.696V79.14H5.484Z' fill='var(--mgr-text-muted)' />

          {/* Right Plus glyph */}
          <path
            d='M217.973 80.076H215.417V82.668H214.397V80.076H211.853V79.152H214.397V76.548H215.417V79.152H217.973V80.076Z'
            fill='var(--mgr-text-muted)'
          />

          {/* Dynamic rotating needle: centered at (109.5, 78) */}
          {hasVariance && (
            <g transform={`rotate(${clampedAngle} 109.5 78)`} className='bx-gauge-needle'>
              <line
                x1='109.5'
                y1='78'
                x2='109.5'
                y2='30'
                stroke='var(--mgr-icon)'
                strokeWidth='3.5'
                strokeLinecap='round'
              />
            </g>
          )}

          {/* Center pivot point */}
          <circle cx='109.5' cy='78' r='6.5' fill='var(--mgr-icon)' />
        </svg>
      </div>

      <div className='bx-gauge-reading'>
        <div className={`bx-gauge-value bx-gauge-value--${tone}`}>{displayValue}</div>
        {pumpSales !== undefined && tankMovement !== undefined && (
          <div className='bx-gauge-subtext'>
            Pump sales {pumpSales.toLocaleString('en-US', { minimumFractionDigits: 1 })} L · Tank
            movement {tankMovement.toLocaleString('en-US', { minimumFractionDigits: 1 })} L
          </div>
        )}
      </div>

      <div className='bx-gauge-badge-wrap'>
        {!hasVariance ? (
          <span className='bx-gauge-badge'>Incomplete readings</span>
        ) : isBalanced ? (
          <span className='bx-gauge-badge bx-gauge-badge--balanced'>Balanced</span>
        ) : (
          <span className={`bx-gauge-badge bx-gauge-badge--${tone}`}>
            {isDeficit ? 'Deficit Discrepancy' : 'Surplus Discrepancy'}
          </span>
        )}
      </div>
    </div>
  )
}

export default VarianceGauge
